import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { usePlatformAuth } from "context/PlatformAuthContext";
import { platformApiService } from "services/platformApi";
import { FaUpload, FaTimes, FaImage } from "react-icons/fa";
import SearchableDropdown from "../../components/SearchableDropdown";
import { getLogoUrl } from "utils/logoUtils";
import { FontSelector } from "../../components/productCustomization/FontSelector";

const StoreForm: React.FC = () => {
 const { t } = useTranslation();
 const { storeId } = useParams<{ storeId: string }>();
 const navigate = useNavigate();
 const { isAuthenticated } = usePlatformAuth();
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [uploadingLogo, setUploadingLogo] = useState(false);
 const [dragActive, setDragActive] = useState(false);
 const logoInputRef = useRef<HTMLInputElement>(null);
 const [domainMode, setDomainMode] = useState<"subdomain" | "custom">("subdomain");
 const [formData, setFormData] = useState({
  name: "",
  domain: "",
  subdomain: "",
  logo: "",
  themePalette: {
   primary: "",
   secondary: "",
   accent: "",
   background: "",
   text: "",
  },
  fontFamily: "",
  businessModel: "plugin-based" as "plugin-based" | "pay-as-you-go",
  transactionFeePercentage: "",
  subscriptionStatus: "incomplete" as "active" | "trialing" | "past_due" | "canceled" | "unpaid" | "incomplete",
  isActive: true,
  adminEmail: "",
  adminName: "",
 });

 // Get platform domain for preview
 const getPlatformDomain = (): string => {
  if (typeof window !== "undefined") {
   const hostname = window.location.hostname;
   if (hostname.includes("localhost") || hostname.includes("local")) {
    return "localhost";
   }
   // Extract base domain (remove subdomain if present)
   const parts = hostname.split(".");
   if (parts.length > 2) {
    return parts.slice(-2).join(".");
   }
   return hostname;
  }
  return "localhost";
 };

 useEffect(() => {
  if (!isAuthenticated) {
   navigate("/platform/login", { replace: true });
   return;
  }

  if (storeId && storeId !== "new") {
   fetchStore();
  }
 }, [storeId, isAuthenticated, navigate]);

 const fetchStore = async () => {
  try {
   setLoading(true);
   setError(null);
   const response = await platformApiService.getStore(storeId!);
   const store = response.data.store;
   if (store) {
    // Determine mode based on what exists
    const hasCustomDomain = !!store.domain && !store.domain.includes(getPlatformDomain());
    setDomainMode(hasCustomDomain ? "custom" : "subdomain");
    
    setFormData({
     name: store.name || "",
     domain: store.domain || "",
     subdomain: store.subdomain || "",
     logo: store.logo || "",
     themePalette: store.themePalette || {
      primary: "",
      secondary: "",
      accent: "",
      background: "",
      text: "",
     },
     fontFamily: store.fontFamily || "",
     businessModel: store.businessModel || "plugin-based",
     transactionFeePercentage: store.transactionFeePercentage?.toString() || "",
     subscriptionStatus: store.subscriptionStatus || "incomplete",
     isActive: store.isActive !== undefined ? store.isActive : true,
     adminEmail: "",
     adminName: "",
    });
   }
  } catch (err: any) {
   console.error("Error fetching store:", err);
   setError(err.response?.data?.error || "Failed to fetch store");
  } finally {
   setLoading(false);
  }
 };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError(null);
  
  // Client-side validation
  if (domainMode === "subdomain" && !formData.subdomain.trim()) {
   setError("Subdomain is required for subdomain-only stores");
   return;
  }
  if (domainMode === "custom" && !formData.domain.trim()) {
   setError("Domain is required for custom domain stores");
   return;
  }
  
  setLoading(true);

  try {
   const submitData: any = {
    name: formData.name,
    businessModel: formData.businessModel,
    subscriptionStatus: formData.subscriptionStatus,
    isActive: formData.isActive,
   };

   // Only include domain/subdomain based on mode
   if (domainMode === "subdomain") {
    if (formData.subdomain) {
     submitData.subdomain = formData.subdomain.toLowerCase().trim();
    }
    // Don't send domain - it will be auto-generated
   } else {
    // Custom domain mode
    if (formData.domain) {
     submitData.domain = formData.domain.toLowerCase().trim();
    }
    if (formData.subdomain) {
     submitData.subdomain = formData.subdomain.toLowerCase().trim();
    }
   }

   if (formData.logo) {
    submitData.logo = formData.logo.trim();
   }

   if (formData.themePalette && Object.values(formData.themePalette).some(v => v)) {
    submitData.themePalette = formData.themePalette;
   }

   if (formData.fontFamily) {
    submitData.fontFamily = formData.fontFamily.trim();
   }

   if (formData.businessModel === "pay-as-you-go" && formData.transactionFeePercentage) {
    submitData.transactionFeePercentage = parseFloat(formData.transactionFeePercentage);
   }

   // Only include admin fields when creating a new store
   if (!storeId || storeId === "new") {
    if (formData.adminEmail) {
     submitData.adminEmail = formData.adminEmail.trim();
    }
    if (formData.adminName) {
     submitData.adminName = formData.adminName.trim();
    }
   }

   if (storeId && storeId !== "new") {
    await platformApiService.updateStore(storeId, submitData);
    navigate("/platform/stores");
   } else {
    const response = await platformApiService.createStore(submitData);
    // Show admin credentials (password is always generated now)
    if (response.data.admin) {
     const adminInfo = response.data.admin;
     alert(`Store created successfully!\n\nStore Admin Credentials:\nEmail: ${adminInfo.email}\nUsername: ${adminInfo.username}\n\nA welcome email has been sent to ${adminInfo.email} with these credentials.`);
    }
    navigate("/platform/stores");
   }
  } catch (err: any) {
   console.error("Error saving store:", err);
   setError(err.response?.data?.error || "Failed to save store");
  } finally {
   setLoading(false);
  }
 };

 if (loading && storeId && storeId !== "new") {
  return <div className="p-8">Loading store...</div>;
 }

 return (
  <div className="p-8">
   <div className="mb-6 flex justify-between items-center">
    <h1 className="text-3xl font-bold">
     {storeId && storeId !== "new" ? "Edit Store" : "Create New Store"}
    </h1>
    <button
     onClick={() => navigate("/platform/stores")}
     className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
    >
     Back to Stores
    </button>
   </div>

   {error && (
    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
     {error}
    </div>
   )}

   <div className="bg-white shadow p-6">
    <form onSubmit={handleSubmit} className="space-y-4">
     {/* General Information Section */}
     <div>
      <h2 className="text-lg font-semibold mb-3">General Information</h2>
      <div className="space-y-4">
       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Store Name <span className="text-red-500">*</span>
         </label>
         <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder={t("common.placeholders.storeName")}
         />
        </div>

        <div className="flex items-end">
         <label className="flex items-center gap-2 cursor-pointer w-full">
          <input
           type="checkbox"
           checked={formData.isActive}
           onChange={(e) =>
            setFormData({ ...formData, isActive: e.target.checked })
           }
           className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm font-medium text-gray-700">{t("admin.active")}</span>
         </label>
        </div>
       </div>

       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t("admin.setupWizard.welcome.storeInfo.businessModel")} <span className="text-red-500">*</span>
         </label>
         <SearchableDropdown
          fetchOptions={async (search: string) => {
           const models = [
            { _id: "plugin-based", label: t("admin.setupWizard.welcome.storeInfo.businessModelPlugin") },
            { _id: "pay-as-you-go", label: t("admin.setupWizard.welcome.storeInfo.businessModelPayAsYouGo") },
           ];
           return models.filter((model) =>
            model.label.toLowerCase().includes(search.toLowerCase())
           );
          }}
          placeholder={t("common.placeholders.selectBusinessModel")}
          value={formData.businessModel}
          getDisplayLabel={(val: string) => {
           const modelMap: Record<string, string> = {
            "plugin-based": "Plugin-Based",
            "pay-as-you-go": "Pay-As-You-Go",
           };
           return modelMap[val] || val;
          }}
          onSelect={(option) => {
           setFormData({ ...formData, businessModel: option._id as any });
          }}
         />
        </div>

        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Subscription Status <span className="text-red-500">*</span>
         </label>
         <SearchableDropdown
          fetchOptions={async (search: string) => {
           const statuses = [
            { _id: "incomplete", label: "Incomplete" },
            { _id: "active", label: "Active" },
            { _id: "trialing", label: "Trialing" },
            { _id: "past_due", label: "Past Due" },
            { _id: "canceled", label: "Canceled" },
            { _id: "unpaid", label: "Unpaid" },
           ];
           return statuses.filter((status) =>
            status.label.toLowerCase().includes(search.toLowerCase())
           );
          }}
          placeholder={t("common.placeholders.selectSubscriptionStatus")}
          value={formData.subscriptionStatus}
          getDisplayLabel={(val: string) => {
           const statusMap: Record<string, string> = {
            incomplete: "Incomplete",
            active: "Active",
            trialing: "Trialing",
            past_due: "Past Due",
            canceled: "Canceled",
            unpaid: "Unpaid",
           };
           return statusMap[val] || val;
          }}
          onSelect={(option) => {
           setFormData({ ...formData, subscriptionStatus: option._id as any });
          }}
         />
        </div>
       </div>

       {formData.businessModel === "pay-as-you-go" && (
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Transaction Fee (%)
         </label>
         <input
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={formData.transactionFeePercentage}
          onChange={(e) =>
           setFormData({ ...formData, transactionFeePercentage: e.target.value })
          }
          className="w-full md:w-1/2 px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="2.5"
         />
        </div>
       )}
      </div>
     </div>

     {/* Store Domain Configuration Section */}
     <div className="border-t pt-4 mt-4">
      <h2 className="text-lg font-semibold mb-3">Store Domain Configuration</h2>
      <div>
       <label className="block text-sm font-medium text-gray-700 mb-2">
        Domain Type <span className="text-red-500">*</span>
       </label>
      
      {/* Mode Selector */}
      <div className="mb-3 flex gap-4">
       <label className="flex items-center gap-2 cursor-pointer">
        <input
         type="radio"
         name="domainMode"
         value="subdomain"
         checked={domainMode === "subdomain"}
         onChange={() => {
          setDomainMode("subdomain");
          // Clear domain when switching to subdomain mode
          setFormData((prev) => ({ ...prev, domain: "" }));
         }}
         className="text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-sm font-medium">Subdomain Only (Recommended)</span>
       </label>
       <label className="flex items-center gap-2 cursor-pointer">
        <input
         type="radio"
         name="domainMode"
         value="custom"
         checked={domainMode === "custom"}
         onChange={() => {
          setDomainMode("custom");
          // Don't clear subdomain when switching to custom domain mode
          // Custom domains can have subdomains (e.g., staging.kadomaison.com)
          // Only clear if user explicitly wants to remove it
         }}
         className="text-indigo-600 focus:ring-indigo-500"
        />
        <span className="text-sm font-medium">Custom Domain</span>
       </label>
      </div>

      {/* Subdomain Mode */}
      {domainMode === "subdomain" && (
       <div className="space-y-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Subdomain <span className="text-red-500">*</span>
         </label>
         <input
          type="text"
          required={domainMode === "subdomain"}
          value={formData.subdomain}
          onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="mystore"
         />
         {formData.subdomain && (
          <div className="mt-1.5 p-1.5 bg-white border border-blue-300 rounded text-xs">
           <span className="text-gray-600">Store will be accessible at: </span>
           <span className="font-mono font-semibold text-blue-700">
            {formData.subdomain.toLowerCase()}.{getPlatformDomain()}
           </span>
          </div>
         )}
        </div>
        <p className="text-xs text-gray-600">
         <strong>How it works:</strong> Enter a subdomain and the system will automatically create a store at <code className="bg-white px-1 rounded">{`{subdomain}.{platform-domain}`}</code>. 
         DNS will be configured automatically if Namecheap DNS is enabled.
        </p>
       </div>
      )}

      {/* Custom Domain Mode */}
      {domainMode === "custom" && (
       <div className="space-y-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
         <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
           Custom Domain <span className="text-red-500">*</span>
          </label>
          <input
           type="text"
           required={domainMode === "custom"}
           value={formData.domain}
           onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
           placeholder="example.com"
          />
         </div>
         <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
           Subdomain <span className="text-gray-500">(optional)</span>
          </label>
          <input
           type="text"
           value={formData.subdomain}
           onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
           className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
           placeholder="store-subdomain"
          />
         </div>
        </div>
        <p className="text-xs text-gray-600">
         <strong>How it works:</strong> Provide your own custom domain. You'll need to configure DNS records to point your domain to this platform.
        </p>
       </div>
      )}
      </div>
     </div>

     {/* Store Admin Account Section */}

     {(!storeId || storeId === "new") && (
      <>
       <div className="border-t pt-4 mt-4">
        <h2 className="text-lg font-semibold mb-3">Store Admin Account</h2>
        <p className="text-xs text-gray-600 mb-3">
         Create an admin account for this store. The admin will be able to access the store admin portal at <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">/admin</code> routes.
        </p>
       </div>

       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Admin Email <span className="text-red-500">*</span>
         </label>
         <input
          type="email"
          required={!storeId || storeId === "new"}
          value={formData.adminEmail}
          onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder="admin@example.com"
         />
        </div>

        <div>
         <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Admin Name <span className="text-gray-500">(optional)</span>
         </label>
         <input
          type="text"
          value={formData.adminName}
          onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          placeholder={t("common.placeholders.storeAdmin")}
         />
        </div>
       </div>
      </>
     )}

     {/* Branding & Theme Section */}
     <div className="border-t pt-4 mt-4">
      <h2 className="text-lg font-semibold mb-3">Branding & Theme</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
       <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
         Store Logo <span className="text-gray-500">(optional)</span>
        </label>
        
        {/* Logo Preview */}
        {formData.logo && (
         <div className="mb-2 relative inline-block">
          <img
           src={getLogoUrl(formData.logo)}
           alt="Store logo"
           className="h-20 w-auto object-contain border border-gray-300 p-1.5 bg-white"
           onError={(e) => {
            // If image fails to load, hide it
            (e.target as HTMLImageElement).style.display = "none";
           }}
          />
          <button
           type="button"
           onClick={() => setFormData({ ...formData, logo: "" })}
           className="absolute -top-1 -right-1 bg-red-600 text-white p-1 hover:bg-red-700"
           title={t("platform.storeForm.removeLogo")}
          >
           <FaTimes className="text-xs" />
          </button>
         </div>
        )}

        {/* Upload Area */}
        <div
         className={`border-2 border-dashed p-3 text-center transition-colors ${
         uploadingLogo
          ? "border-blue-500 bg-blue-50 opacity-50 pointer-events-none"
          : dragActive
          ? "border-indigo-500 bg-blue-50"
          : "border-gray-300 hover:border-gray-400 cursor-pointer"
        }`}
        onClick={() => logoInputRef.current?.click()}
        onDragEnter={(e) => {
         e.preventDefault();
         e.stopPropagation();
         setDragActive(true);
        }}
        onDragLeave={(e) => {
         e.preventDefault();
         e.stopPropagation();
         setDragActive(false);
        }}
        onDragOver={(e) => {
         e.preventDefault();
         e.stopPropagation();
        }}
        onDrop={async (e) => {
         e.preventDefault();
         e.stopPropagation();
         setDragActive(false);
         
         const file = e.dataTransfer.files?.[0];
         if (!file || !file.type.startsWith("image/")) {
          setError("Please drop an image file");
          return;
         }

         setUploadingLogo(true);
         try {
          const response = await platformApiService.uploadImage(file);
          if (response.data.url) {
           setFormData({ ...formData, logo: response.data.url });
          }
         } catch (err: any) {
          console.error("Logo upload error:", err);
          setError(err.response?.data?.error || "Failed to upload logo");
         } finally {
          setUploadingLogo(false);
         }
        }}
       >
        <input
         ref={logoInputRef}
         type="file"
         accept="image/*"
         onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;

          setUploadingLogo(true);
          try {
           const response = await platformApiService.uploadImage(file);
           if (response.data.url) {
            setFormData({ ...formData, logo: response.data.url });
           }
          } catch (err: any) {
           console.error("Logo upload error:", err);
           setError(err.response?.data?.error || "Failed to upload logo");
          } finally {
           setUploadingLogo(false);
           if (logoInputRef.current) {
            logoInputRef.current.value = "";
           }
          }
         }}
         className="hidden"
        />
         <FaImage className="mx-auto text-2xl text-gray-400 mb-1" />
         <p className="text-xs text-gray-600 mb-1.5">
          {uploadingLogo ? "Uploading..." : formData.logo ? "Click to change" : "Click or drag to upload"}
         </p>
         <button
          type="button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          disabled={uploadingLogo}
         >
          <FaUpload className="text-xs" />
          {uploadingLogo ? "Uploading..." : "Select"}
         </button>
        </div>
       </div>

       <div>
        <FontSelector
         label="Font Family"
         value={formData.fontFamily}
         onChange={(e) => setFormData({ ...formData, fontFamily: e.target.value })}
         placeholder="Default (System Font)"
         outputFormat="css"
        />
       </div>
      </div>

      <div className="mt-4">
       <label className="block text-sm font-medium text-gray-700 mb-1.5">
        Color Palette
       </label>
       <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="flex-1">
         <label className="block text-xs text-gray-600 mb-1">Primary Color</label>
         <div className="flex gap-1.5">
          <input
           type="color"
           value={formData.themePalette.primary || "#000000"}
           onChange={(e) => setFormData({
            ...formData,
            themePalette: { ...formData.themePalette, primary: e.target.value }
           })}
           className="h-9 w-14 border border-gray-300 rounded cursor-pointer"
          />
          <input
           type="text"
           value={formData.themePalette.primary || ""}
           onChange={(e) => {
            let value = e.target.value.trim();
            if (value && !value.startsWith("#")) {
             value = "#" + value;
            }
            if (/^#?[0-9A-Fa-f]{0,6}$/.test(value) || value === "" || value === "#") {
             const normalizedValue = value && value !== "#" ? (value.startsWith("#") ? value : "#" + value) : "";
             setFormData({
              ...formData,
              themePalette: { ...formData.themePalette, primary: normalizedValue }
             });
            }
           }}
           placeholder="#000000"
           className="flex-1 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-mono"
          />
         </div>
        </div>
        <div className="flex-1">
         <label className="block text-xs text-gray-600 mb-1">Secondary Color</label>
         <div className="flex gap-1.5">
          <input
           type="color"
           value={formData.themePalette.secondary || "#000000"}
           onChange={(e) => setFormData({
            ...formData,
            themePalette: { ...formData.themePalette, secondary: e.target.value }
           })}
           className="h-9 w-14 border border-gray-300 rounded cursor-pointer"
          />
          <input
           type="text"
           value={formData.themePalette.secondary || ""}
           onChange={(e) => {
            let value = e.target.value.trim();
            if (value && !value.startsWith("#")) {
             value = "#" + value;
            }
            if (/^#?[0-9A-Fa-f]{0,6}$/.test(value) || value === "" || value === "#") {
             const normalizedValue = value && value !== "#" ? (value.startsWith("#") ? value : "#" + value) : "";
             setFormData({
              ...formData,
              themePalette: { ...formData.themePalette, secondary: normalizedValue }
             });
            }
           }}
           placeholder="#000000"
           className="flex-1 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-mono"
          />
         </div>
        </div>
        <div className="flex-1">
         <label className="block text-xs text-gray-600 mb-1">Accent Color</label>
         <div className="flex gap-1.5">
          <input
           type="color"
           value={formData.themePalette.accent || "#000000"}
           onChange={(e) => setFormData({
            ...formData,
            themePalette: { ...formData.themePalette, accent: e.target.value }
           })}
           className="h-9 w-14 border border-gray-300 rounded cursor-pointer"
          />
          <input
           type="text"
           value={formData.themePalette.accent || ""}
           onChange={(e) => {
            let value = e.target.value.trim();
            if (value && !value.startsWith("#")) {
             value = "#" + value;
            }
            if (/^#?[0-9A-Fa-f]{0,6}$/.test(value) || value === "" || value === "#") {
             const normalizedValue = value && value !== "#" ? (value.startsWith("#") ? value : "#" + value) : "";
             setFormData({
              ...formData,
              themePalette: { ...formData.themePalette, accent: normalizedValue }
             });
            }
           }}
           placeholder="#000000"
           className="flex-1 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-mono"
          />
         </div>
        </div>
        <div className="flex-1">
         <label className="block text-xs text-gray-600 mb-1">Background Color</label>
         <div className="flex gap-1.5">
          <input
           type="color"
           value={formData.themePalette.background || "#ffffff"}
           onChange={(e) => setFormData({
            ...formData,
            themePalette: { ...formData.themePalette, background: e.target.value }
           })}
           className="h-9 w-14 border border-gray-300 rounded cursor-pointer"
          />
          <input
           type="text"
           value={formData.themePalette.background || ""}
           onChange={(e) => {
            let value = e.target.value.trim();
            if (value && !value.startsWith("#")) {
             value = "#" + value;
            }
            if (/^#?[0-9A-Fa-f]{0,6}$/.test(value) || value === "" || value === "#") {
             const normalizedValue = value && value !== "#" ? (value.startsWith("#") ? value : "#" + value) : "";
             setFormData({
              ...formData,
              themePalette: { ...formData.themePalette, background: normalizedValue }
             });
            }
           }}
           placeholder="#ffffff"
           className="flex-1 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-mono"
          />
         </div>
        </div>
        <div className="flex-1">
         <label className="block text-xs text-gray-600 mb-1">Text Color</label>
         <div className="flex gap-1.5">
          <input
           type="color"
           value={formData.themePalette.text || "#000000"}
           onChange={(e) => setFormData({
            ...formData,
            themePalette: { ...formData.themePalette, text: e.target.value }
           })}
           className="h-9 w-14 border border-gray-300 rounded cursor-pointer"
          />
          <input
           type="text"
           value={formData.themePalette.text || ""}
           onChange={(e) => {
            let value = e.target.value.trim();
            if (value && !value.startsWith("#")) {
             value = "#" + value;
            }
            if (/^#?[0-9A-Fa-f]{0,6}$/.test(value) || value === "" || value === "#") {
             const normalizedValue = value && value !== "#" ? (value.startsWith("#") ? value : "#" + value) : "";
             setFormData({
              ...formData,
              themePalette: { ...formData.themePalette, text: normalizedValue }
             });
            }
           }}
           placeholder="#000000"
           className="flex-1 px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs font-mono"
          />
         </div>
        </div>
       </div>
      </div>
     </div>

     <div className="flex gap-2">
      <button
       type="submit"
       disabled={loading}
       className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
      >
       {loading ? "Saving..." : storeId && storeId !== "new" ? "Update Store" : "Create Store"}
      </button>
      <button
       type="button"
       onClick={() => navigate("/platform/stores")}
       className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300"
      >
       Cancel
      </button>
     </div>
    </form>
   </div>
  </div>
 );
};

export default StoreForm;

